import { action, atom } from '@reatom/framework'
import i18next from 'i18next'

import { Result } from '@blade/std-lib'
import { UserShort } from '@root/entities/user-short'
import { UnknownError } from '@root/shared/core/Error/Error'
import { reatomRequest } from '@root/shared/lib'
import { showErrorSnackbarAction } from '@root/shared/ui'
import { confirmAction } from '@root/shared/ui/confirm-dialog'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { reatomBackOfficeUserChangeActivityRequestAction } from './reatom-back-office-user-change-activity-request-action'

import type { BackOfficeUser } from '@root/entities/back-office/back-office-user'

type ToggleActivityAction = 'unblock' | 'block'

export const reatomBackOfficeUserActivityToggleAction = (args: {
  backOfficeUser: BackOfficeUser
}) => {
  const name = UserShort.someName(args.backOfficeUser)

  const nexActionAtom = atom<ToggleActivityAction>(
    (ctx) => (ctx.spy(args.backOfficeUser.blocked) ? 'unblock' : 'block'),
    `${name}.nexActionAtom`,
  )

  // eslint-disable-next-line @reatom/unit-naming-rule
  const toggleRequestAtom = reatomRequest({
    execAction: reatomBackOfficeUserChangeActivityRequestAction,
    prepare: (ctx) => ({
      delugeId: args.backOfficeUser.delugeId,
      action: ctx.get(nexActionAtom),
    }),
    onResult: (ctx, result) => {
      Result.match(result, {
        onFailure: (error) => showErrorSnackbarAction(ctx, error),
        onSuccess: (value) => {
          if (value.success) {
            showSnackbarAction(ctx, {
              intent: 'success',
              text:
                ctx.get(nexActionAtom) === 'unblock'
                  ? i18next.t(
                      'back_office.user.has_been_successfully_unblocked',
                      { name },
                    )
                  : i18next.t(
                      'back_office.user.has_been_successfully_blocked',
                      { name },
                    ),
            })

            args.backOfficeUser.blocked(ctx, (x) => !x)
          } else {
            showErrorSnackbarAction(ctx, new UnknownError({}))
          }
        },
      })
    },
    concurrently: true,
  })

  const toggleAction = action(async (ctx) => {
    const description =
      ctx.get(nexActionAtom) === 'unblock'
        ? i18next.t('back_office.user.block_user_confirm_unblock', {
            name,
          })
        : i18next.t('back_office.user.block_user_confirm_block', {
            name,
          })
    const confirm = await ctx.schedule(() =>
      confirmAction(ctx, {
        describedBy: description,
        description,
      }),
    )

    if (Result.isSuccess(confirm)) {
      return await ctx.schedule(() => toggleRequestAtom.execute(ctx))
    }
  }, `${name}.toggleAction`)

  return {
    nexActionAtom,
    toggleAction,
    toggleRequestAtom,
  }
}
